import { Grid, Stack } from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ReviewSuggestionsBannerEntryPoint } from '@/modules/aiSuggestions/reviewSuggestions/ReviewSuggestionsBannerEntryPoint';
import { ClientOnboardingDeliverableSelectionModal } from '@/modules/clientOnboarding/ClientOnboardingDeliverableModal/ClientOnboardingDeliverableSelectionModal';
import {
  ONBOARDING_SEARCH_PARAM,
  ONBOARDING_VARIANT_SEARCH_PARAM,
} from '@/modules/clientOnboarding/ClientOnboardingDeliverableModal/ClientOnboardingDeliverableSelectionModal.constants';
import { ClientOnboardingDeliverableVariant } from '@/modules/clientOnboarding/types';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { HouseholdEstateValueMetricsPortrait } from '@/modules/household/HouseholdEstateValueMetrics/HouseholdEstateValueMetricsPortrait';
import { HouseholdExemptionsOverview } from '@/modules/household/HouseholdExemptionsOverview';
import { SmartReviewOnboardingBanner } from '@/modules/onboarding/smartReview/SmartReviewOnboardingBanner';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useValidationQuestionsState } from '@/modules/validationQuestions/hooks/useValidationQuestionsState';
import { ValidationQuestionsBanner } from '@/modules/validationQuestions/ValidationQuestionsBanner/ValidationQuestionsBanner';
import { ClientOnboardingSection } from '@/pages/ClientPages/ClientOverviewPage/ClientOnboardingSection';

import { ClientDetailsOverviewPageCard } from './ClientDetailsOverviewPageCard';
import { useClientDetailsOverviewPageQuery } from './hooks/useClientDetailsOverviewPageQuery';
import { useLayoutDefinition } from './hooks/useLayoutDefinition';
import { ProjectionContentSection } from './ProjectionContentSection';

export interface ClientOverviewPageContentProps {
  /** Loading and progress content supplied by the enhanced overview. */
  setupContent?: ReactNode;
  showReviewSuggestions?: boolean;
  householdId: string;
  household: ReturnType<typeof useClientDetailsOverviewPageQuery>['household'];
  hasEntities: ReturnType<
    typeof useClientDetailsOverviewPageQuery
  >['hasEntities'];
  assetsSummary: ReturnType<
    typeof useClientDetailsOverviewPageQuery
  >['assetsSummary'];
}

function OverviewOnboarding({
  householdId,
  household,
  setupContent,
  showReviewSuggestions,
}: Pick<
  ClientOverviewPageContentProps,
  'householdId' | 'household' | 'setupContent' | 'showReviewSuggestions'
>) {
  const isAIEnabled = useAICapabilitiesEnabled();
  const enhanced = useFeatureFlag('enhanced_household_onboarding_flow');
  const { outstandingConversationId, answeredAt, isWaitingForAnswers } =
    useValidationQuestionsState(householdId);
  return (
    <>
      {!enhanced && (
        <SmartReviewOnboardingBanner
          householdId={householdId}
          householdClients={household?.possiblePrimaryClients.map((client) => ({
            firstName: client.firstName,
            lastName: client.lastName,
            stateCode: client.address?.stateCode ?? '',
          }))}
          validationQuestionsAnsweredAt={answeredAt}
          isWaitingForValidationQuestions={isWaitingForAnswers}
        />
      )}
      {!enhanced && (
        <ValidationQuestionsBanner
          householdId={householdId}
          conversationId={outstandingConversationId}
        />
      )}
      {setupContent}
      <ReviewSuggestionsBannerEntryPoint
        showBanner={!enhanced && showReviewSuggestions}
      />
      {isAIEnabled && <ClientOnboardingSection householdId={householdId} />}
    </>
  );
}

export function ClientOverviewPageContent({
  householdId,
  household,
  hasEntities,
  assetsSummary,
  setupContent,
  showReviewSuggestions = true,
}: ClientOverviewPageContentProps) {
  const { gridTemplateAreas, gridTemplateColumns, gridTemplateRows } =
    useLayoutDefinition();
  const [searchParams, setSearchParams] = useSearchParams();
  const onboardingModalQueryParamSet = useMemo(() => {
    return searchParams.get(ONBOARDING_SEARCH_PARAM) === 'true';
  }, [searchParams]);
  const onboardingVariant = searchParams.get(
    ONBOARDING_VARIANT_SEARCH_PARAM
  ) as ClientOnboardingDeliverableVariant | null;
  const [onboardingModalIsOpen, setOnboardingModalIsOpen] = useState(
    onboardingModalQueryParamSet
  );
  // this useEffect is here to make sure that the modal is opened when the query
  // param is set even if the user is already on the page
  useEffect(() => {
    if (onboardingModalQueryParamSet) {
      setOnboardingModalIsOpen(true);
    }
  }, [onboardingModalQueryParamSet]);

  return (
    <>
      <Stack spacing={2} height="100%">
        <OverviewOnboarding
          householdId={householdId}
          household={household}
          setupContent={setupContent}
          showReviewSuggestions={showReviewSuggestions}
        />
        <Grid
          display="grid"
          gap={2}
          container
          gridTemplateColumns={gridTemplateColumns}
          gridTemplateRows={gridTemplateRows}
          gridTemplateAreas={gridTemplateAreas}
        >
          <Grid item gridArea="total-assets">
            <ClientDetailsOverviewPageCard includeHelpContentTrigger>
              <HouseholdEstateValueMetricsPortrait
                metrics={household?.estateValueMetrics}
                assetsSummary={assetsSummary}
                householdId={householdId}
                hasEntities={hasEntities}
              />
            </ClientDetailsOverviewPageCard>
          </Grid>

          <ProjectionContentSection
            householdId={householdId}
            household={household}
          />

          <Grid item gridArea="exemptions">
            <ClientDetailsOverviewPageCard>
              <HouseholdExemptionsOverview household={household} />
            </ClientDetailsOverviewPageCard>
          </Grid>
        </Grid>
      </Stack>
      <ClientOnboardingDeliverableSelectionModal
        isOpen={onboardingModalIsOpen}
        onClose={() => {
          setOnboardingModalIsOpen(false);
          const newParams = new URLSearchParams(searchParams);
          newParams.delete(ONBOARDING_SEARCH_PARAM);
          newParams.delete(ONBOARDING_VARIANT_SEARCH_PARAM);
          setSearchParams(newParams);
        }}
        initialVariant={onboardingVariant} // No initial variant, modal opens with four deliverable card options
      />
    </>
  );
}
